import { memo } from "react";

import { classNames } from "helpers";
import CardHeader from "./common/CardHeader";
import BarTooltip, {
  BarTooltipRow,
  BarTooltipTitle,
} from "./common/HoverPopup";
import { text } from "./common/dashboardPalette";

// Gradijenti barova (jak vrh → svjetlije dno), usklađeni s glavnim dashboardom.
// Pune klase u stringovima da ih Tailwind JIT vidi.
const SERIES_GRADIENT = [
  "bg-gradient-to-b from-sky-400 to-sky-200",
  "bg-gradient-to-b from-indigo-400 to-indigo-200",
  "bg-gradient-to-b from-teal-400 to-teal-200",
  "bg-gradient-to-b from-amber-400 to-amber-200",
  "bg-gradient-to-b from-rose-400 to-rose-200",
];
const PRIMARY_GRADIENT = "bg-gradient-to-b from-sky-400 to-sky-200";

export interface CssBar {
  label: string;
  shortLabel?: string;
  value: number;
}

interface CssBarChartProps {
  title: string;
  subtitle?: string;
  bars: CssBar[];
  formatValue: (value: number) => string;
  tooltipLabel: string;
  colorMode?: "series" | "primary";
  showTopValue?: boolean;
  footer?: React.ReactNode;
}

/**
 * CSS bar-graf usklađen s glavnim dashboardom (CardHeader + vertikalni stupci,
 * dark BarTooltip na hover, dashboardPalette boje, growUp animacija) — bez recharts-a.
 */
const CssBarChart: React.FC<CssBarChartProps> = ({
  title,
  subtitle,
  bars,
  formatValue,
  tooltipLabel,
  colorMode = "primary",
  showTopValue = false,
  footer,
}) => {
  const maxValue = Math.max(...bars.map((b) => b.value), 1);

  return (
    <div className="rounded-sm px-4 py-5 bg-white h-full flex flex-col border border-slate-200">
      <CardHeader title={title} subtitle={subtitle} className="mb-8" />

      <div className="mx-auto w-full flex-1 flex flex-col">
        <div className="flex justify-between gap-1.5 mb-2 flex-1 min-h-[120px]">
          {bars.map((bar, i) => {
            const heightPercent = maxValue > 0 ? (bar.value / maxValue) * 100 : 0;
            const barColor =
              colorMode === "series"
                ? SERIES_GRADIENT[i % SERIES_GRADIENT.length]
                : PRIMARY_GRADIENT;
            return (
              <div
                key={`${bar.label}-${i}`}
                className="flex flex-col items-center gap-1 flex-1 h-full group/bar relative"
              >
                {showTopValue && (
                  <span className={`text-xs ${text.muted} whitespace-nowrap`}>{formatValue(bar.value)}</span>
                )}
                <div className="w-full flex-1 flex items-end">
                  <div
                    className={classNames(
                      "w-full rounded-sm origin-bottom animate-growUp transition-opacity group-hover/bar:opacity-80",
                      barColor
                    )}
                    style={{ height: `${Math.max(heightPercent, 4)}%` }}
                  />
                </div>
                <BarTooltip>
                  <BarTooltipTitle>{bar.label}</BarTooltipTitle>
                  <BarTooltipRow label={tooltipLabel} value={formatValue(bar.value)} emphasis="positive" />
                </BarTooltip>
              </div>
            );
          })}
        </div>
        <div className="flex justify-between gap-1.5">
          {bars.map((bar, i) => (
            <span key={`${bar.label}-lbl-${i}`} className={`text-xs ${text.subtle} flex-1 text-center truncate`}>
              {bar.shortLabel ?? bar.label}
            </span>
          ))}
        </div>
      </div>

      {footer && <div className="mt-4">{footer}</div>}
    </div>
  );
};

export default memo(CssBarChart);
